import React, {useState} from "react";
import {CUSTOMER_FIGURE_STATUS_ACHIEVED, CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED, CUSTOMER_FIGURE_STATUS_IN_PROGRESS, CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED, CUSTOMER_FIGURE_STATUS_NOTHING, getCustomerFigureDisplayStatus, getCustomerFigureStatusNameById, getNeedToLevelIcon} from "../../utility/utility";
import Button from "../UI/Button";
import {saveCustomerFigure} from "../../api/customerApiClient";
import {toast} from "react-toastify";
import {useFigureProgressTracker} from "../../hooks/useFigureProgressTracker";
import {faEdit, faTimes, faColumns} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

const generateTables = (
    customersWithCustomerFigures,
    thematicsWithFigures,
    handleStatusIconClick,
    selectedStatuses,
    clickedCustomerFigure,
    handleConsistentlyAchievedButtonClick,
    handleAchievedButtonClick,
    handleNotAchievedButtonClick,
    handleInProgressButtonClick,
    handleCommentButtonClick,
    showComments,
    batchEditRows,
    handleBatchEditClick,
    selectedCustomers,
    handleCustomerCheckboxChange,
    handleBatchStatusUpdate,
    handleSelectAllChange,
    batchEditColumns,
    handleBatchEditColumnClick,
    selectedFigures,
    handleFigureCheckboxChange,
    handleBatchStatusUpdateByCustomer,
    handleSelectAllFiguresChange
) => {
    const needToLevelIcon = getNeedToLevelIcon();
    const tables = [];

    thematicsWithFigures.forEach((thematicWithFigures) => {
        const tableHeader = [];
        const tableData = [];
        const thematicFigures = thematicWithFigures.thematicRequirements;

        // Header
        // Thematic name, ...Customer name
        tableHeader.push(
            <tr className="border-b" key="headers">
                <th className="text-left p-1 px-2">{thematicWithFigures.name}</th>
                {customersWithCustomerFigures.map((customerWithFigures) => {
                    const isColumnEditMode = batchEditColumns.includes(customerWithFigures.customerId);
                    const selectedFigureIds = selectedFigures[customerWithFigures.customerId] || [];
                    
                    return (
                        <th className="text-left p-1 px-2" key={`${customerWithFigures.customerId}`}>
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                                <div className="flex justify-between items-center">
                                    <span>
                                        {`${customerWithFigures.fullName}`}
                                    </span>
                                    {isColumnEditMode ? (
                                        <div
                                            className="cursor-pointer text-red-500 ml-2"
                                            onClick={() => handleBatchEditColumnClick(customerWithFigures.customerId)}
                                            title="Cancel"
                                        >
                                            <FontAwesomeIcon icon={faTimes} className="text-lg" />
                                        </div>
                                    ) : (
                                        <div 
                                            className="cursor-pointer ml-2" 
                                            onClick={() => handleBatchEditColumnClick(customerWithFigures.customerId)}
                                            title="Edit figures for this customer"
                                        >
                                            <FontAwesomeIcon icon={faColumns} className="text-sm" />
                                        </div>
                                    )}
                                </div>
                                {customerWithFigures.levelName && (
                                    <span className="text-xs font-thin">
                                        {customerWithFigures.levelName}
                                    </span>
                                )}
                                {isColumnEditMode && (
                                    <div className="mt-2">
                                        <Button title="Excellent" handleClick={() => handleBatchStatusUpdateByCustomer(customerWithFigures.customerId, CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED)}>Excellent</Button>
                                        <Button title="Yes" handleClick={() => handleBatchStatusUpdateByCustomer(customerWithFigures.customerId, CUSTOMER_FIGURE_STATUS_ACHIEVED)}>Yes</Button>
                                        <Button title="No" handleClick={() => handleBatchStatusUpdateByCustomer(customerWithFigures.customerId, CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED)}>No</Button>
                                        <Button title="Pending" handleClick={() => handleBatchStatusUpdateByCustomer(customerWithFigures.customerId, CUSTOMER_FIGURE_STATUS_IN_PROGRESS)}>Pending</Button>
                                        
                                        <div className="mt-2 flex items-center justify-center">
                                            <input
                                                type="checkbox"
                                                checked={thematicFigures.length > 0 && thematicFigures.every(figure => selectedFigureIds.includes(figure.figureId))}
                                                onChange={(e) => handleSelectAllFiguresChange(customerWithFigures.customerId, e.target.checked, thematicFigures)}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">Select All</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </th>
                    );
                })}
                <th className="text-left p-1 px-2"></th>
            </tr>
        );

        // Create table rows with figure name and customer thematicFigures
        thematicFigures.forEach((thematicFigure) => {
            // Check if the selectedStatuses matches the status of at least one customer figure for this thematic figure
            const hasSelectedStatus = customersWithCustomerFigures.some((customerWithCustomerFigures) => {
                const customerFigure = customerWithCustomerFigures.figureRequirements.find(
                    (customerFigure) => customerFigure.figureId === thematicFigure.figureId
                );
                const status = customerFigure ? customerFigure.status : CUSTOMER_FIGURE_STATUS_NOTHING;

                return selectedStatuses?.includes(status);
            });

            // If the selectedStatuses does not match, skip this row
            if (selectedStatuses?.length && !hasSelectedStatus) {
                return;
            }

            const rowData = [{name : thematicFigure.name, category : thematicFigure.categoryName, grip : thematicFigure.gripName, level : thematicFigure.levelName, levelRequirements : thematicFigure.levelRequirements}];

            customersWithCustomerFigures.forEach((customerWithCustomerFigures) => {
                const customerFigure = customerWithCustomerFigures.figureRequirements.find(
                    (customerFigure) => customerFigure.figureId === thematicFigure.figureId
                );
                const status = customerFigure ? customerFigure.status : null;
                const comment = customerFigure ? customerFigure.comment : null;
                const levelRequirements = customerFigure ? customerFigure.levelRequirements : [];
                rowData.push({status, customerId : customerWithCustomerFigures.customerId, comment : comment, levelRequirements : levelRequirements});
            });

            // Table row
            // Figure, ...customer figure progress
            const row = rowData.map((data, index) => {
                // Figures
                if (index === 0) {
                    const leverReq = thematicFigure.levelRequirements.map(req => `${req.levelName}: ${getCustomerFigureStatusNameById(req.requiredStatus)}`).join(', ');

                    return (
                        <td className="text-left p-1 px-2" key={`${thematicFigure.figureId}-${index}`}>
                            <div>
                                <span>{data.level && needToLevelIcon} {data.name}</span>
                                <span className="text-xs font-thin ml-1">{data.category} {data.grip ? ' | ' + data.grip : null}</span>
                            </div>
                            {leverReq &&
                                <div>
                                    <span className="text-xs font-thin ml-1">{leverReq}</span>
                                </div>
                            }
                        </td>
                    );
                }
                // Customer figures
                else {
                    const isStatusIconClicked =
                        clickedCustomerFigure?.customerId === data.customerId &&
                        clickedCustomerFigure?.figureId === thematicFigure.figureId;

                    const isBatchEditMode = batchEditRows.includes(thematicFigure.figureId);
                    const isCustomerSelected = selectedCustomers[thematicFigure.figureId]?.includes(data.customerId);
                    const tdBgClass = isStatusIconClicked ? "bg-white-100" : (data.levelRequirements.length > 0 ? "bg-red-50" : "");

                    return (
                        <td
                            onClick={() => {
                                if (!isBatchEditMode) {
                                    handleStatusIconClick(data.customerId, thematicFigure.figureId);
                                }
                            }}
                            className={`p-1 px-2 text-center ${tdBgClass}`}
                            key={`${thematicFigure.figureId}-${index}`}
                        >
                            {isBatchEditMode ? (
                                <div className="w-full inline-block align-top">
                                    <input
                                        type="checkbox"
                                        checked={isCustomerSelected}
                                        onChange={() => handleCustomerCheckboxChange(thematicFigure.figureId, data.customerId)}
                                        className="mr-2"
                                    />
                                    {getCustomerFigureDisplayStatus(data.status)}
                                </div>
                            ) : batchEditColumns.includes(data.customerId) ? (
                                <div className="w-full inline-block align-top">
                                    <input
                                        type="checkbox"
                                        checked={(selectedFigures[data.customerId] || []).includes(thematicFigure.figureId)}
                                        onChange={() => handleFigureCheckboxChange(data.customerId, thematicFigure.figureId)}
                                        className="mr-2"
                                    />
                                    {getCustomerFigureDisplayStatus(data.status)}
                                </div>
                            ) : (
                                <div className="w-full inline-block align-top hover:cursor-pointer">
                                    {getCustomerFigureDisplayStatus(data.status)}
                                </div>
                            )}
                            <div className="relative align-bottom">
                                {showComments && data.comment && <small>{data.comment}</small>}
                            </div>
                            {isStatusIconClicked && !isBatchEditMode && (
                                <div className="p-1 px-2 text-center" key={`${thematicFigure.figureId}-${index}-buttons`}>
                                    {data.status !== CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED && (
                                        <Button title="Excellent" handleClick={() => handleConsistentlyAchievedButtonClick(data.customerId, thematicFigure.figureId)}>Excellent</Button>
                                    )}
                                    {data.status !== CUSTOMER_FIGURE_STATUS_ACHIEVED && (
                                        <Button title="Yes" handleClick={() => handleAchievedButtonClick(data.customerId, thematicFigure.figureId)}>Yes</Button>
                                    )}
                                    {data.status !== CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED && (
                                        <Button title="No" handleClick={() => handleNotAchievedButtonClick(data.customerId, thematicFigure.figureId)}>No</Button>
                                    )}
                                    {data.status !== CUSTOMER_FIGURE_STATUS_IN_PROGRESS && (
                                        <Button title="Pending" handleClick={() => handleInProgressButtonClick(data.customerId, thematicFigure.figureId)}>Pending</Button>
                                    )}
                                    <Button title="Comment" handleClick={() => handleCommentButtonClick(data.customerId, thematicFigure.figureId, data.status)}>Comment</Button>
                                </div>
                            )}
                        </td>
                    );
                }
            });

            // Add edit/save column at the end of the row
            const isBatchEditMode = batchEditRows.includes(thematicFigure.figureId);
            const selectedCustomerIds = selectedCustomers[thematicFigure.figureId] || [];

            // Calculate if all customers are selected for this row
            const allCustomersInRow = customersWithCustomerFigures.map(customer => customer.customerId);
            const allSelected = allCustomersInRow.length > 0 &&
                allCustomersInRow.every(id => selectedCustomerIds.includes(id));

            const editColumn = (
                <td className="p-1 px-2 text-center" key={`${thematicFigure.figureId}-edit`}>
                    {isBatchEditMode ? (
                        <div>
                            <div className="flex justify-end mb-2">
                                <div
                                    className="cursor-pointer text-red-500"
                                    onClick={() => handleBatchEditClick(thematicFigure.figureId)}
                                    title="Cancel"
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="text-lg"
                                    />
                                </div>
                            </div>
                            <Button title="Excellent" handleClick={() => handleBatchStatusUpdate(thematicFigure.figureId, CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED)}>Excellent</Button>
                            <Button title="Yes" handleClick={() => handleBatchStatusUpdate(thematicFigure.figureId, CUSTOMER_FIGURE_STATUS_ACHIEVED)}>Yes</Button>
                            <Button title="No" handleClick={() => handleBatchStatusUpdate(thematicFigure.figureId, CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED)}>No</Button>
                            <Button title="Pending" handleClick={() => handleBatchStatusUpdate(thematicFigure.figureId, CUSTOMER_FIGURE_STATUS_IN_PROGRESS)}>Pending</Button>

                            <div className="mt-2 flex items-center justify-center">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={(e) => handleSelectAllChange(thematicFigure.figureId, e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm">Select All</span>
                            </div>
                        </div>
                    ) : (
                        <div className="cursor-pointer" onClick={() => handleBatchEditClick(thematicFigure.figureId)}>
                            <FontAwesomeIcon
                                icon={faEdit}
                                className="text-lg"
                            />
                        </div>
                    )}
                </td>
            );

            row.push(editColumn);
            tableData.push(<tr className="border-b hover:bg-gray-100" key={thematicFigure.figureId}>{row}</tr>);
        });

        // Add table to tables array
        const table = (
            <div style={{maxHeight : '640px'}} className="overflow-y-auto" key={thematicWithFigures.thematicId}>
                <table className="bg-white shadow-md rounded my-4">
                    <thead className="sticky top-0 bg-white">{tableHeader}</thead>
                    <tbody>{tableData}</tbody>
                </table>
            </div>
        );
        tables.push(table);
    });

    return tables;
};

const FigureProgressTrackerTable = (props) => {
    const [submitCount, setSubmitCount] = useState(0);
    const [clickedCustomerFigure, setClickedCustomerFigure] = useState(null);
    const [batchEditRows, setBatchEditRows] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState({});
    const [batchEditColumns, setBatchEditColumns] = useState([]);
    const [selectedFigures, setSelectedFigures] = useState({});
    const {customersWithCustomerFigures, thematicsWithFigures} = useFigureProgressTracker(
        props.customerIds,
        props.thematicIds,
        +props.showComments + submitCount + props.submitCount
    );

    const handleStatusIconClick = (customerId, figureId) => {
        const isClicked =
            clickedCustomerFigure?.customerId === customerId && clickedCustomerFigure?.figureId === figureId;

        if (isClicked) {
            setClickedCustomerFigure(null);
        }
        else {
            setClickedCustomerFigure({customerId, figureId});
        }
    };

    const handleConsistentlyAchievedButtonClick = (customerId, figureId) => {
        saveCustomerFigure([customerId], [figureId], CUSTOMER_FIGURE_STATUS_CONSISTENTLY_ACHIEVED, new Date().toISOString().substr(0, 10), null)
            .then(() => {
                setClickedCustomerFigure(null);
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success("Success!");

                if (props?.handleStatusButtonClick) {
                    props.handleStatusButtonClick();
                }
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const handleAchievedButtonClick = (customerId, figureId) => {
        saveCustomerFigure([customerId], [figureId], CUSTOMER_FIGURE_STATUS_ACHIEVED, new Date().toISOString().substr(0, 10), null)
            .then(() => {
                setClickedCustomerFigure(null);
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success("Success!");

                if (props?.handleStatusButtonClick) {
                    props.handleStatusButtonClick();
                }
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const handleNotAchievedButtonClick = (customerId, figureId) => {
        saveCustomerFigure([customerId], [figureId], CUSTOMER_FIGURE_STATUS_NOT_ACHIEVED, new Date().toISOString().substr(0, 10), null)
            .then(() => {
                setClickedCustomerFigure(null);
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success("Success!");

                if (props?.handleStatusButtonClick) {
                    props.handleStatusButtonClick();
                }
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const handleInProgressButtonClick = (customerId, figureId) => {
        saveCustomerFigure([customerId], [figureId], CUSTOMER_FIGURE_STATUS_IN_PROGRESS, new Date().toISOString().substr(0, 10), null)
            .then(() => {
                setClickedCustomerFigure(null);
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success("Success!");

                if (props?.handleStatusButtonClick) {
                    props.handleStatusButtonClick();
                }
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const handleCommentButtonClick = (customerId, figureId, currentStatus) => {
        setClickedCustomerFigure(null);
        props.handleStatusButtonIconClick(customerId, figureId, currentStatus);
    };

    const handleBatchEditClick = (figureId) => {
        // Turn off column editing mode when enabling row editing
        setBatchEditColumns([]);
        setSelectedFigures({});
        
        setBatchEditRows(prevRows => {
            if (prevRows.includes(figureId)) {
                // If figureId is already in the array, remove it
                return prevRows.filter(id => id !== figureId);
            } else {
                // If figureId is not in the array, add it
                return [...prevRows, figureId];
            }
        });

        // Reset selected customers for this figureId when toggling edit mode off
        if (batchEditRows.includes(figureId)) {
            setSelectedCustomers(prev => ({
                ...prev,
                [figureId]: []
            }));
        }
    };

    const handleBatchEditColumnClick = (customerId) => {
        // Turn off row editing mode when enabling column editing
        setBatchEditRows([]);
        setSelectedCustomers({});
        
        setBatchEditColumns(prevColumns => {
            if (prevColumns.includes(customerId)) {
                // If customerId is already in the array, remove it
                return prevColumns.filter(id => id !== customerId);
            } else {
                // If customerId is not in the array, add it
                return [...prevColumns, customerId];
            }
        });

        // Reset selected figures for this customerId when toggling edit mode off
        if (batchEditColumns.includes(customerId)) {
            setSelectedFigures(prev => ({
                ...prev,
                [customerId]: []
            }));
        }
    };
    
    const handleFigureCheckboxChange = (customerId, figureId) => {
        setSelectedFigures(prev => {
            const currentSelected = prev[customerId] || [];
            if (currentSelected.includes(figureId)) {
                return {
                    ...prev,
                    [customerId] : currentSelected.filter(id => id !== figureId)
                };
            } else {
                return {
                    ...prev,
                    [customerId] : [...currentSelected, figureId]
                };
            }
        });
    };
    
    const handleSelectAllFiguresChange = (customerId, isChecked, figures) => {
        if (isChecked) {
            // Select all figures for this customer
            const allFigureIds = figures.map(figure => figure.figureId);
            setSelectedFigures(prev => ({
                ...prev,
                [customerId] : allFigureIds
            }));
        } else {
            // Deselect all figures for this customer
            setSelectedFigures(prev => ({
                ...prev,
                [customerId] : []
            }));
        }
    };
    
    const handleBatchStatusUpdateByCustomer = (customerId, status) => {
        const figureIds = selectedFigures[customerId] || [];
        if (figureIds.length === 0) {
            toast.warning("Please select at least one figure");
            return;
        }

        saveCustomerFigure([customerId], figureIds, status, new Date().toISOString().substr(0, 10), null)
            .then(() => {
                // Clear batch edit mode for this column
                setBatchEditColumns(prev => prev.filter(id => id !== customerId));
                setSelectedFigures(prev => ({
                    ...prev,
                    [customerId] : []
                }));
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success(`Successfully updated ${figureIds.length} figure(s)!`);

                if (props?.handleStatusButtonClick) {
                    props.handleStatusButtonClick();
                }
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const handleSelectAllChange = (figureId, isChecked) => {
        if (isChecked) {
            // Only select customers if this figure belongs to one of the displayed thematics
            const figureBelongsToThematic = thematicsWithFigures.some((thematic) =>
                thematic.thematicRequirements.some((figure) => figure.figureId === figureId)
            );

            const allCustomerIds = figureBelongsToThematic
                ? customersWithCustomerFigures.map((customer) => customer.customerId)
                : [];

            setSelectedCustomers(prev => ({
                ...prev,
                [figureId] : allCustomerIds
            }));
        }
        else {
            // Deselect all customers for this figure
            setSelectedCustomers(prev => ({
                ...prev,
                [figureId] : []
            }));
        }
    };

    const handleCustomerCheckboxChange = (figureId, customerId) => {
        setSelectedCustomers(prev => {
            const currentSelected = prev[figureId] || [];
            if (currentSelected.includes(customerId)) {
                return {
                    ...prev,
                    [figureId] : currentSelected.filter(id => id !== customerId)
                };
            }
            else {
                return {
                    ...prev,
                    [figureId] : [...currentSelected, customerId]
                };
            }
        });
    };

    const handleBatchStatusUpdate = (figureId, status) => {
        const customerIds = selectedCustomers[figureId] || [];
        if (customerIds.length === 0) {
            toast.warning("Please select at least one customer");
            return;
        }

        saveCustomerFigure(customerIds, [figureId], status, new Date().toISOString().substr(0, 10), null)
            .then(() => {
                // Clear batch edit mode for this row
                setBatchEditRows(prev => prev.filter(id => id !== figureId));
                setSelectedCustomers(prev => ({
                    ...prev,
                    [figureId] : []
                }));
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success(`Successfully updated ${customerIds.length} customer(s)!`);

                if (props?.handleStatusButtonClick) {
                    props.handleStatusButtonClick();
                }
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const tables = generateTables(
        customersWithCustomerFigures,
        thematicsWithFigures,
        handleStatusIconClick,
        props.selectedStatues,
        clickedCustomerFigure,
        handleConsistentlyAchievedButtonClick,
        handleAchievedButtonClick,
        handleNotAchievedButtonClick,
        handleInProgressButtonClick,
        handleCommentButtonClick,
        props.showComments,
        batchEditRows,
        handleBatchEditClick,
        selectedCustomers,
        handleCustomerCheckboxChange,
        handleBatchStatusUpdate,
        handleSelectAllChange,
        batchEditColumns,
        handleBatchEditColumnClick,
        selectedFigures,
        handleFigureCheckboxChange,
        handleBatchStatusUpdateByCustomer,
        handleSelectAllFiguresChange
    );

    return <div className="overflow-x-auto">{tables}</div>;
};

export default FigureProgressTrackerTable;
